import { z } from "zod";
import { adClickAttributionSchema, metaEventContextSchema } from "./analytics";

const emailSchema = z.string().email("Invalid email format").toLowerCase().trim();

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(100, "Password too long");

const phoneSchema = z
  .string()
  .regex(/^\+?[1-9]\d{1,14}$/, "Invalid phone number format")
  .min(7, "Phone number too short")
  .max(16, "Phone number too long");

const nameSchema = z
  .string()
  .min(2, "Name must be at least 2 characters")
  .max(50, "Name too long")
  .trim();

const registerAccountSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: passwordSchema,
  phoneNumber: phoneSchema.optional().nullable(),
  accountType: z.enum(["default", "google"]).default("default"),
});

const registerProfileSchema = z.object({
  firstName: nameSchema,
  lastName: nameSchema,
  locale: z.string().min(2).max(32).optional(),
  displayName: z.string().max(120).optional().nullable(),
  organizationName: z.string().max(200).optional().nullable(),
  country: z
    .string()
    .length(2)
    .regex(/^[A-Z]{2}$/)
    .optional(),
  utmSource: z.string().max(100).optional(),
});

const registerConsentSchema = z
  .object({
    acceptTerms: z.boolean().optional(),
    acceptPrivacy: z.boolean().optional(),
    newsletter: z.boolean().optional(),
  })
  .optional();

const registerNestedSchema = z
  .object({
    account: registerAccountSchema,
    profile: registerProfileSchema,
    consent: registerConsentSchema,
    silent: z.boolean().optional(),
    attribution: adClickAttributionSchema.nullable().optional(),
    meta: metaEventContextSchema.optional(),
  })
  .refine((data) => data.account.password === data.account.confirmPassword, {
    message: "Passwords don't match",
    path: ["account", "confirmPassword"],
  });

function normalizeRegisterBody(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return raw;
  const o = raw as Record<string, unknown>;
  if (
    o.account &&
    typeof o.account === "object" &&
    o.profile &&
    typeof o.profile === "object"
  ) {
    return raw;
  }
  if (
    typeof o.email === "string" &&
    typeof o.password === "string" &&
    typeof o.firstName === "string"
  ) {
    return {
      account: {
        email: o.email,
        password: o.password,
        confirmPassword: o.confirmPassword ?? o.password,
        phoneNumber: o.phoneNumber ?? o.phone,
        accountType: o.accountType ?? "default",
      },
      profile: {
        firstName: o.firstName,
        lastName: o.lastName,
        locale: o.locale,
        displayName: o.displayName,
        organizationName: o.organizationName,
        country: o.country,
        utmSource: o.utmSource,
      },
      consent:
        typeof o.acceptTerms === "boolean" ||
        typeof o.acceptPrivacy === "boolean" ||
        typeof o.newsletter === "boolean"
          ? {
              acceptTerms: o.acceptTerms === true,
              acceptPrivacy:
                o.acceptPrivacy === undefined ? undefined : o.acceptPrivacy === true,
              newsletter: o.newsletter === undefined ? undefined : o.newsletter === true,
            }
          : undefined,
      silent: o.silent === undefined ? undefined : Boolean(o.silent),
      attribution: o.attribution,
      meta: o.meta,
    };
  }
  return raw;
}

export const registerSchema = z.preprocess(normalizeRegisterBody, registerNestedSchema);

export const loginSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  rememberMe: z.boolean().optional(),
});

export const authWithGoogleSchema = z.object({
  code: z.string(),
  attribution: adClickAttributionSchema.nullable().optional(),
  meta: metaEventContextSchema.optional(),
});

export const authorizeWithGoogleSchema = authWithGoogleSchema.extend({
  authorizationId: z.string().optional(),
});

export const sendPhoneCodeSchema = z.object({
  phone: phoneSchema,
});

export const verifyPhoneCodeSchema = z.object({
  phone: phoneSchema,
  code: z
    .string()
    .length(6, "Code must be 6 digits")
    .regex(/^\d+$/, "Code must contain only numbers"),
});

export const checkPhoneAvailabilitySchema = z.object({
  phone: phoneSchema,
});

export const checkEmailAvailabilitySchema = z.object({
  email: emailSchema,
});

export const verifyEmailCodeSchema = z.object({
  code: z
    .string()
    .length(6, "Code must be 6 digits")
    .regex(/^\d+$/, "Code must contain only numbers"),
});

export const verifyEmailChangeSchema = z.object({
  token: z.string().min(1, "Token is required"),
});

export const changePasswordSchema = z
  .object({
    currentPassword: passwordSchema,
    newPassword: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "New password must be different from current password",
    path: ["newPassword"],
  });

export const resetPasswordRequestSchema = z.object({
  email: emailSchema,
});

export const confirmResetPasswordSchema = z
  .object({
    token: z.string().min(1),
    newPassword: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

function normalizeOAuthQuery(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return raw;
  const o = raw as Record<string, unknown>;
  return {
    clientId: o.clientId ?? o.client_id,
    redirectUri: o.redirectUri ?? o.redirect_uri,
    scope: o.scope,
    state: o.state,
    codeChallenge: o.codeChallenge ?? o.code_challenge,
    codeChallengeMethod: o.codeChallengeMethod ?? o.code_challenge_method,
  };
}

export const initializeAuthorizationSchema = z.preprocess(
  normalizeOAuthQuery,
  z.object({
    clientId: z.string().min(1, "Client ID is required"),
    redirectUri: z.string().min(1, "Redirect URI is required"),
    scope: z.string().optional().default("read write"),
    state: z.string().optional(),
    codeChallenge: z.string().optional(),
    codeChallengeMethod: z.enum(["S256", "plain"]).optional(),
  }),
);

export const getAuthorizationStatusSchema = z.object({
  id: z.string().min(1, "Authorization ID is required"),
});

export const confirmAuthorizationSchema = z.object({
  authorizationId: z.string().min(1, "Authorization ID is required"),
  userId: z.string().min(1, "User ID is required").optional(),
  accountId: z.string().min(1).optional(),
  scopes: z.array(z.string()).optional(),
});

export const denyAuthorizationSchema = z.object({
  authorizationId: z.string().min(1, "Authorization ID is required"),
  reason: z.string().optional(),
});

function normalizeOAuthTokenBody(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return raw;
  const o = raw as Record<string, unknown>;
  return {
    grantType: o.grantType ?? o.grant_type,
    code: o.code,
    redirectUri: o.redirectUri ?? o.redirect_uri,
    clientId: o.clientId ?? o.client_id,
    clientSecret: o.clientSecret ?? o.client_secret,
    codeVerifier: o.codeVerifier ?? o.code_verifier,
    refreshToken: o.refreshToken ?? o.refresh_token,
    scope: o.scope,
    token: o.token,
    tokenTypeHint: o.tokenTypeHint ?? o.token_type_hint,
  };
}

export const exchangeCodeForTokenSchema = z.preprocess(
  normalizeOAuthTokenBody,
  z.object({
    grantType: z.literal("authorization_code"),
    code: z.string().min(1, "Authorization code is required"),
    redirectUri: z.string().min(1, "Redirect URI is required"),
    clientId: z.string().min(1, "Client ID is required"),
    clientSecret: z.string().optional(),
    codeVerifier: z.string().optional(),
  }),
);

export const refreshTokenSchema = z.preprocess(
  normalizeOAuthTokenBody,
  z.object({
    grantType: z.literal("refresh_token"),
    refreshToken: z.string().min(1, "Refresh token is required"),
    clientId: z.string().min(1, "Client ID is required"),
    clientSecret: z.string().optional(),
    scope: z.string().optional(),
  }),
);

export const revokeTokenSchema = z.preprocess(
  normalizeOAuthTokenBody,
  z.object({
    token: z.string().min(1, "Token is required"),
    tokenTypeHint: z.enum(["access_token", "refresh_token"]).optional(),
    clientId: z.string().min(1, "Client ID is required"),
    /** Optional for public clients; required for confidential clients at runtime. */
    clientSecret: z.string().optional(),
  }),
);

export const introspectTokenSchema = z.preprocess(
  normalizeOAuthTokenBody,
  z.object({
    token: z.string().min(1, "Token is required"),
    tokenTypeHint: z.enum(["access_token", "refresh_token"]).optional(),
    clientId: z.string().min(1, "Client ID is required"),
    clientSecret: z.string().optional(),
  }),
);

export type RegisterRequest = z.infer<typeof registerSchema>;
export type LoginRequest = z.infer<typeof loginSchema>;
export type AuthWithGoogleRequest = z.infer<typeof authWithGoogleSchema>;
export type AuthorizeWithGoogleRequest = z.infer<typeof authorizeWithGoogleSchema>;
export type SendPhoneCodeRequest = z.infer<typeof sendPhoneCodeSchema>;
export type VerifyPhoneCodeRequest = z.infer<typeof verifyPhoneCodeSchema>;
export type CheckPhoneAvailabilityRequest = z.infer<typeof checkPhoneAvailabilitySchema>;
export type CheckEmailAvailabilityRequest = z.infer<typeof checkEmailAvailabilitySchema>;
export type VerifyEmailCodeRequest = z.infer<typeof verifyEmailCodeSchema>;
export type VerifyEmailChangeRequest = z.infer<typeof verifyEmailChangeSchema>;
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;
export type ConfirmResetPasswordRequest = z.infer<typeof confirmResetPasswordSchema>;
export type ConfirmAuthorizationRequest = z.infer<typeof confirmAuthorizationSchema>;
